// Hook to manage all households the user belongs to and the active household selection.
// - Fetches all memberships via getUserHouseholds()
// - Persists the active household ID in localStorage under ACTIVE_HOUSEHOLD_KEY
// - Exposes switchHousehold(id) to change the active selection and flush caches

import { useCallback, useEffect } from "react";
import {
  getUserHouseholds,
  type HouseholdWithRole,
} from "../services/householdService";
import { useQuery, useQueryClient } from "./useDataCache";
import { clearLastKnownHouseholdId, USER_HOUSEHOLD_ID_KEY } from "./useUser";

export const ACTIVE_HOUSEHOLD_KEY = "pantry_active_household_id";

export const ALL_HOUSEHOLDS_QUERY_KEY = ["allHouseholds"] as const;

export function useHouseholds() {
  const queryClient = useQueryClient();

  const { data: households = [], isLoading } = useQuery<HouseholdWithRole[]>({
    queryKey: ALL_HOUSEHOLDS_QUERY_KEY,
    queryFn: getUserHouseholds,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // When the household list loads, validate the stored override.
  // If it points to a household the user no longer belongs to, clear it
  // and let useUser fall back to the DB-fetched default.
  useEffect(() => {
    if (isLoading || households.length === 0) return;
    const override = localStorage.getItem(ACTIVE_HOUSEHOLD_KEY);
    if (override && !households.some((h) => String(h.id) === override)) {
      localStorage.removeItem(ACTIVE_HOUSEHOLD_KEY);
      // Clear the module-level cache so useUser re-resolves from the DB
      clearLastKnownHouseholdId();
      queryClient.invalidateQueries({ queryKey: USER_HOUSEHOLD_ID_KEY });
    }
  }, [households, isLoading, queryClient]);

  const switchHousehold = useCallback(
    async (householdId: string) => {
      // Always store as a string so comparisons with localStorage are consistent.
      const idStr = String(householdId);
      // Persist the selection
      localStorage.setItem(ACTIVE_HOUSEHOLD_KEY, idStr);

      // Clear the module-level last-known cache in useUser so it doesn't override
      clearLastKnownHouseholdId();

      // Overwrite the React Query cache for the household ID key immediately
      queryClient.setQueryData(USER_HOUSEHOLD_ID_KEY, idStr);

      // Invalidate household-specific caches so the new household's data loads
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["household"] }),
        queryClient.invalidateQueries({ queryKey: ["inventory"] }),
        queryClient.invalidateQueries({ queryKey: ["shoppingList"] }),
        queryClient.invalidateQueries({ queryKey: ["favorites"] }),
      ]);
    },
    [queryClient],
  );

  return {
    households,
    isLoading,
    switchHousehold,
  };
}
